import { QueryClient } from '@tanstack/react-query';
import { MomentInput } from 'moment';
import { getContext, put, takeLatest } from 'redux-saga/effects';
import { AdvancedSetting } from 'api/advanced-settings/settings-types';
import { ProjectId } from 'api/models';
import { Project } from 'api/project';
import { Sprint } from 'api/sprint-planning/types';
import { DependentEntityPayload, TaskAPI } from 'api/tasks';
import { StoreActionProps } from 'data/core';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { formatDate, getDueDateFromGantt } from 'data/projects/gantt/dates';
import { getGanttTaskId } from 'data/projects/gantt/ids';
import {
  getPhaseList,
  getSprintList,
  getTaskList,
  normaliseTasks,
} from 'data/projects/gantt/list-helpers';
import { updateTaskDates } from 'data/projects/sagas/project/date-shift';
import { Task } from 'data/tasks';
import {
  getQueryAdvancedSettingsConfigKey,
  getNewDependenciesEnabledSetting,
} from 'queries/advanced-settings/advanced-settings';
import {
  getProjectDependencyMapKey,
  getProjectSprintsQueryKey,
  getProjectTasksQueryKey,
} from 'queries/common';
import { getTaskDetailFieldsQueryKey } from 'queries/task';
import {
  DateShiftUpdates,
  DependencyTypesEnumReverse,
  EntityDateShiftUpdate,
  EntityType,
  getDateShiftsForEntity,
  List,
} from 'utils/date-shift';
import {
  createDependencySagaAction,
  CreateDependencyActionPayload,
  dependencyCreatedAction,
  dependencyCreationThroughTimeline,
} from '../actions';
import { updateTasksDates } from '../slice';

export function populateTaskWithNullDates(
  projectId: number,
  taskId: number,
  taskList: List,
  taskUpdates: EntityDateShiftUpdate[],
  taskStore?: any,
  payloadTaskUpdates?: DateShiftUpdates,
) {
  // Exit early if there's no payload data for date shifts.
  if (!payloadTaskUpdates) {
    return;
  }

  let startDate: MomentInput | undefined;
  let dueDate: MomentInput | undefined;

  // Use taskStore if available to get the current task record from Gantt.
  if (taskStore) {
    const ganttId = getGanttTaskId(taskId);
    const record = taskStore.getById(ganttId);

    // Reset calculated flags for start and due dates.
    record.set('isCalculatedStartDate', false);
    record.set('isCalculatedEndDate', false);

    // Determine start and due dates using payload data or fallback to record data.
    startDate =
      payloadTaskUpdates[taskId]?.startDate ??
      formatDate(record.getStartDate());
    dueDate =
      payloadTaskUpdates[taskId]?.dueDate ?? getDueDateFromGantt(record);
  }
  // Fallback to taskList if taskStore is not provided.
  else if (taskList[taskId]) {
    startDate =
      payloadTaskUpdates[taskId]?.startDate ?? taskList[taskId]?.startDate;
    dueDate = payloadTaskUpdates[taskId]?.dueDate ?? taskList[taskId]?.dueDate;
  }

  // If we have valid dates, update the task and push the update.
  if (startDate && dueDate) {
    TaskAPI.updateTask(projectId, taskId, { startDate, dueDate });
    taskUpdates.push({ id: taskId, startDate, dueDate });
  }
}

function* handleEmptyTaskDates(
  action: StoreActionProps<CreateDependencyActionPayload>,
): Generator {
  const { payload } = action;

  if (!payload) {
    return;
  }

  const {
    taskId,
    projectId,
    dependentTaskId,
    canAssignDates,
    alignPhaseDates,
    isDatesPresent,
    newDependenciesSettingEnabled: payloadNewDepsEnabled,
  } = payload;

  let { taskUpdates: payloadTaskUpdates } = payload;

  if (!canAssignDates || !taskId || isDatesPresent) {
    return;
  }

  const queryClient = (yield getQueryClientContext()) as QueryClient;
  const project = queryClient.getQueryData<Project>(ProjectId(projectId));

  if (!project) {
    return;
  }

  const sprints = queryClient.getQueryData<Sprint[]>(
    getProjectSprintsQueryKey(ProjectId(projectId)),
  );
  const queryTasks = queryClient.getQueryData<Task[]>(
    getProjectTasksQueryKey(ProjectId(projectId)),
  );
  const tasks = normaliseTasks(queryTasks) as Task[];
  const accountSettings = queryClient.getQueryData<AdvancedSetting[]>(
    getQueryAdvancedSettingsConfigKey(),
  );
  const computedNewDepsEnabled =
    getNewDependenciesEnabledSetting(accountSettings);

  const { projectPhases } = project;
  const phaseList = getPhaseList(projectPhases);
  const taskList = getTaskList(tasks, phaseList);
  const sprintList = getSprintList(sprints ?? [], phaseList);

  const ganttRef = (yield getContext('ganttRef')) as any;
  const taskStore = ganttRef?.instance?.taskStore;

  const _task = taskList[taskId];
  const taskUpdates: EntityDateShiftUpdate[] = [];

  // Helper to update a task's dates if they are missing.
  const updateIfMissing = (id: number) => {
    const task = taskList[id];

    if (!task.hasStartDate || !task.hasDueDate) {
      populateTaskWithNullDates(
        projectId,
        id,
        taskList,
        taskUpdates,
        taskStore,
        payloadTaskUpdates,
      );
    }
  };

  // Initialize payloadTaskUpdates if not already provided.
  if (!payloadTaskUpdates) {
    let entityPayload: any;

    if (!dependentTaskId && payloadNewDepsEnabled) {
      entityPayload = {
        id: taskId,
        dependentOn: [...(taskList[taskId]?.dependentOn || [])],
        type: EntityType.Task,
        depth: taskList[taskId].depth,
      };
    } else if (dependentTaskId) {
      entityPayload = computedNewDepsEnabled
        ? {
            id: taskId,
            dependentOn: [...(taskList[taskId]?.dependentOn || [])],
            type: EntityType.Task,
            depth: taskList[taskId].depth,
          }
        : {
            id: dependentTaskId,
            dependencyFor: [
              ...(taskList[dependentTaskId]?.dependencyFor || []),
              {
                id: taskId,
                dependencyType: 2,
                lag: null,
              },
            ],
            type: EntityType.Task,
            depth: taskList[dependentTaskId].depth,
          };
    }
    const [_, _taskUpdates] = getDateShiftsForEntity(
      phaseList,
      taskList,
      sprintList,
      entityPayload,
      {
        alignPhaseWithTask: alignPhaseDates ?? true,
        dependencyOperationsDone: true,
        enabledNewDependencies: computedNewDepsEnabled,
      },
    );

    payloadTaskUpdates = _taskUpdates;
  }

  // Process tasks based on whether a dependent task exists.
  if (!dependentTaskId && payloadNewDepsEnabled) {
    // Update the main task and its direct dependencies.
    updateIfMissing(_task.id);
    (_task.dependentOn || []).forEach((dependency) => {
      updateIfMissing(dependency.id);
    });
    // Update any other tasks returned from the date shifts.
    Object.values(payloadTaskUpdates).forEach((item: any) => {
      if (item.id !== _task.id) {
        updateIfMissing(item.id);
      }
    });
  } else if (dependentTaskId) {
    // In case of a dependency relation, update both the dependent and the main task.
    const _dependentTask = taskList[dependentTaskId];

    updateIfMissing(_dependentTask.id);
    updateIfMissing(_task.id);
    // Update other tasks from the date shifts, excluding the main and dependent tasks.
    Object.values(payloadTaskUpdates).forEach((item: any) => {
      if (item.id !== _dependentTask.id && item.id !== _task.id) {
        updateIfMissing(item.id);
      }
    });

    if (taskUpdates.length > 0) {
      yield put(updateTasksDates(taskUpdates));
      updateTaskDates(projectId, taskUpdates, queryClient);
    }
  }
}

function* createDependencySaga(
  action: StoreActionProps<CreateDependencyActionPayload>,
): Generator {
  const { payload } = action;

  const queryClient = (yield getQueryClientContext()) as QueryClient;

  if (payload) {
    const {
      taskId,
      projectId,
      dependentTaskId,
      isSync,
      toggleUpdating,
      handleNullDates,
      dependencyType,
      newDependenciesSettingEnabled,
    } = payload;

    if (dependentTaskId && dependencyType !== undefined) {
      const data: DependentEntityPayload[] = [
        {
          entityId: taskId,
          dependentEntityId: dependentTaskId,
          dependencyType: DependencyTypesEnumReverse[dependencyType],
          lag: null,
          operation: 'ADD',
        },
      ];

      yield put(
        dependencyCreationThroughTimeline({
          id: taskId,
          type: EntityType.Task,
          payload: {
            successorId: taskId,
            dependencyType,
            lag: null,
            predecessorId: dependentTaskId,
          },
        }),
      );
      let task: Task;

      if (newDependenciesSettingEnabled) {
        task = (yield TaskAPI.updateDependentTasks(taskId, data)) as Task;
      } else {
        task = (yield TaskAPI.createDependentTask(
          projectId,
          taskId,
          {
            dependentTaskIds: [dependentTaskId],
          },
          isSync,
        )) as Task;
      }

      yield queryClient.invalidateQueries(
        getProjectDependencyMapKey(projectId),
      );

      // Refetch task details default fields for both tasks involved in the dependency
      yield queryClient.refetchQueries(
        getTaskDetailFieldsQueryKey({
          queryString: 'task-details-default-fields',
          projectId,
          taskId,
        }),
      );
      yield queryClient.refetchQueries(
        getTaskDetailFieldsQueryKey({
          queryString: 'task-details-default-fields',
          projectId,
          taskId: dependentTaskId,
        }),
      );
      if (isSync && toggleUpdating && task) {
        toggleUpdating(false);
      }

      if (handleNullDates) {
        yield handleEmptyTaskDates(action);
      }
    }
  }
}
/*
Earlier empty task dates were handled using the same action, but after introducing
synchronous date updates ( adding delay in frontend), handleEmptyDates is being called inside creation saga.

The second action we see is being called from drawer, and does not require the deliberate delay there.
 */
export function* watchCreateDependencySaga(): Generator {
  yield takeLatest(createDependencySagaAction.type, createDependencySaga);
  yield takeLatest(dependencyCreatedAction.type, handleEmptyTaskDates);
}
